import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import {
  Color,
  DoubleSide,
  Group,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  TextureLoader,
  Vector3,
  type Texture,
} from 'three'
import { config } from '../config'
import { cubeWorldPosRef } from './cubeWorldPos'
import { useAppStore } from '../store/appStore'
import { useSceneStore } from '../store/sceneStore'

const _baseColor = new Color('#2a221c')
const _glowColor = new Color('#b8835a')

// plane 局部三轴（从 mesh.matrixWorld 列向量抽取）
const _planeRight = new Vector3()
const _planeUp = new Vector3()
const _planeNormal = new Vector3()
const _planePos = new Vector3()
const _toCube = new Vector3()

/** 加载可选的 dropbox 贴图（缺失时返回 null） */
function useDropBoxTexture(): Texture | null {
  const [tex, setTex] = useState<Texture | null>(null)
  useEffect(() => {
    const path = config.assets.paths.dropBoxTexture
    let cancelled = false
    fetch(path, { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) throw new Error('no texture')
        return new Promise<Texture>((resolve, reject) =>
          new TextureLoader().load(path, resolve, undefined, reject),
        )
      })
      .then((t) => {
        if (!cancelled) setTex(t)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  return tex
}

export function DropBox() {
  const groupRef = useRef<Group>(null)
  const planeRef = useRef<Mesh>(null)
  const triggeredRef = useRef(false)
  const proximityRef = useRef(0)
  const wallTex = useDropBoxTexture()
  const [px, py, pz] = config.dropBox.position
  const tiltRad = -(config.dropBox.tiltDeg * Math.PI) / 180
  const side = config.dropBox.side
  const cubeSize = config.cube.size
  const lateralThreshold = config.dropBox.lateralRatio
  const approachDist = config.dropBox.approachDist
  const setDropProx = useSceneStore((s) => s.setDropProximity)

  const material = useMemo(
    () =>
      new MeshStandardMaterial({
        color: _baseColor.clone(),
        emissive: _glowColor.clone(),
        emissiveIntensity: 0,
        metalness: 0.25,
        roughness: 0.55,
        side: DoubleSide,
        map: wallTex,
        transparent: true,
      }),
    [wallTex],
  )

  useFrame(() => {
    const m = planeRef.current
    if (!m) return
    m.updateMatrixWorld()
    // 从 plane 的 matrixWorld 列向量直接抽出局部三轴（避免手算 sin/cos 出错）
    _planeRight.setFromMatrixColumn(m.matrixWorld, 0)
    _planeUp.setFromMatrixColumn(m.matrixWorld, 1)
    _planeNormal.setFromMatrixColumn(m.matrixWorld, 2)
    m.getWorldPosition(_planePos)

    const cubePos = cubeWorldPosRef.current
    _toCube.subVectors(cubePos, _planePos)
    const u = _toCube.dot(_planeRight)
    const v = _toCube.dot(_planeUp)
    const d = _toCube.dot(_planeNormal) // 正 = plane 前方（朝摄像机），负 = 后方

    // 横向重叠面积 / cube footprint 面积（cube axis-aligned 近似）
    const overlapU = Math.max(
      0,
      Math.min(u + cubeSize / 2, side / 2) - Math.max(u - cubeSize / 2, -side / 2),
    )
    const overlapV = Math.max(
      0,
      Math.min(v + cubeSize / 2, side / 2) - Math.max(v - cubeSize / 2, -side / 2),
    )
    const lateralRatio = (overlapU * overlapV) / (cubeSize * cubeSize)

    // 底面已跨过 plane：cube 中心进入 plane 后侧 cubeSize/2 之内
    const bottomCrossed = d <= cubeSize / 2

    // prox：横向对齐 × 距离接近度，连续驱动 emissive / 抬升 / Label
    const approachProx = MathUtils.clamp(1 - d / approachDist, 0, 1)
    const prox = MathUtils.clamp(lateralRatio * approachProx, 0, 1)
    proximityRef.current = prox
    setDropProx(prox)

    const appPhase = useAppStore.getState().phase
    const isShrinking =
      appPhase === 'shrinking' || appPhase === 'curtain' || appPhase === 'done'

    if (!isShrinking && lateralRatio >= lateralThreshold && bottomCrossed) {
      if (!triggeredRef.current) {
        triggeredRef.current = true
        useAppStore.getState().triggerLab()
      }
    } else if (!isShrinking) {
      triggeredRef.current = false
    }

    material.emissiveIntensity = isShrinking ? 1.2 : prox * 1.2

    if (groupRef.current) {
      if (!isShrinking) {
        const breath = 0.02 * Math.sin(performance.now() * 0.004)
        const lift = prox * 0.12 + breath * prox
        groupRef.current.position.set(
          px + lift * _planeNormal.x,
          py + lift * _planeNormal.y,
          pz + lift * _planeNormal.z,
        )
      }
      // shrink 阶段缩小消失；intro/returning 恢复 1
      const target = isShrinking ? 0 : 1
      const sLerp = isShrinking ? 0.05 : 0.18
      const s = groupRef.current.scale
      s.x = MathUtils.lerp(s.x, target, sLerp)
      s.y = MathUtils.lerp(s.y, target, sLerp)
      s.z = MathUtils.lerp(s.z, target, sLerp)
    }
  })

  return (
    <group ref={groupRef} position={[px, py, pz]} rotation={[tiltRad, 0, 0]}>
      <mesh ref={planeRef} material={material} renderOrder={50}>
        <planeGeometry args={[side, side]} />
      </mesh>
      <DropLabel proximityRef={proximityRef} side={side} />
    </group>
  )
}

function DropLabel({
  proximityRef,
  side,
}: {
  proximityRef: { current: number }
  side: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  useFrame(() => {
    if (!ref.current) return
    const p = proximityRef.current
    ref.current.style.opacity = String(Math.min(1, p * 1.4))
  })
  return (
    <Html position={[0, side * 0.7, 0]} center distanceFactor={3} zIndexRange={[2, 1]}>
      <div ref={ref} className="dropbox-label">
        {config.dropBox.label}
      </div>
    </Html>
  )
}
